import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Ticket, Clock, Save } from "lucide-react";
import { toast } from "sonner";

const emptyExam = () => ({
  title: "",
  description: "",
  exam_type: "LGS",
  subject: "",
  grade_level: "",
  start_at: new Date(Date.now() + 3600e3).toISOString().slice(0, 16),
  end_at: new Date(Date.now() + 3 * 3600e3).toISOString().slice(0, 16),
  duration_minutes: 60,
  negative_marking: 0.25,
  credit_cost: 0,
  question_ids: [],
});

export default function AdminMockExams() {
  const [exams, setExams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExam());
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`${V1}/mock-exams?upcoming=false&limit=100`).then((r) => setExams(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing(null);
    setForm(emptyExam());
    setOpen(true);
  };

  const startEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || "",
      description: e.description || "",
      exam_type: e.exam_type,
      subject: e.subject || "",
      grade_level: e.grade_level || "",
      start_at: (e.start_at || "").slice(0, 16),
      end_at: (e.end_at || "").slice(0, 16),
      duration_minutes: e.duration_minutes ?? 60,
      negative_marking: e.negative_marking ?? 0.25,
      credit_cost: e.credit_cost ?? 0,
      question_ids: e.question_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        duration_minutes: parseInt(form.duration_minutes) || 60,
        negative_marking: parseFloat(form.negative_marking) || 0,
        credit_cost: parseInt(form.credit_cost) || 0,
      };
      if (editing) {
        await api.patch(`${V1}/mock-exams/${editing.id}`, payload);
        toast.success("Sınav güncellendi");
      } else {
        await api.post(`${V1}/mock-exams`, payload);
        toast.success("Sınav oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sınavı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`${V1}/mock-exams/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-mock-exams-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Sınav Yönetimi</div>
          <h1 className="font-display text-3xl font-bold">Deneme Sınavları</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Ücretsiz veya kredi ile satın alınabilen premium denemeler oluşturun.
          </p>
        </div>
        <Button onClick={startNew} data-testid="new-mock-exam-btn">
          <Plus className="h-4 w-4" /> Yeni Deneme
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Başlık</th>
              <th className="text-left p-3">Tür</th>
              <th className="text-left p-3">Başlangıç</th>
              <th className="text-left p-3">Süre</th>
              <th className="text-left p-3">Kredi</th>
              <th className="text-left p-3">Soru</th>
              <th className="text-right p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {exams.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  Henüz deneme sınavı yok.
                </td>
              </tr>
            )}
            {exams.map((e) => (
              <tr key={e.id} className="border-t border-border" data-testid={`admin-mock-row-${e.id}`}>
                <td className="p-3">
                  <div className="font-medium">{e.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{e.description}</div>
                </td>
                <td className="p-3">{e.exam_type}</td>
                <td className="p-3">{e.start_at ? new Date(e.start_at).toLocaleString("tr-TR") : "-"}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {e.duration_minutes} dk
                  </span>
                </td>
                <td className="p-3">
                  {(e.credit_cost || 0) > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent font-bold text-xs"
                      data-testid={`admin-mock-credit-${e.id}`}
                    >
                      <Ticket className="h-3 w-3" /> {e.credit_cost} Kredi
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Ücretsiz</span>
                  )}
                </td>
                <td className="p-3">{e.question_count ?? (e.question_ids?.length || 0)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(e)} data-testid={`edit-mock-${e.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`delete-mock-${e.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="mock-exam-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Deneme Sınavını Düzenle" : "Yeni Deneme Sınavı"}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="mock-form-title" />
            </div>
            <div className="md:col-span-2">
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="mock-form-description" />
            </div>
            <div>
              <Label>Sınav Türü</Label>
              <Select value={form.exam_type} onValueChange={(v) => setF("exam_type", v)}>
                <SelectTrigger data-testid="mock-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LGS">LGS</SelectItem>
                  <SelectItem value="TYT">TYT</SelectItem>
                  <SelectItem value="AYT">AYT</SelectItem>
                  <SelectItem value="GENEL">Genel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sınıf</Label>
              <Input value={form.grade_level} onChange={(e) => setF("grade_level", e.target.value)} placeholder="8 / 12 vb." data-testid="mock-form-grade" />
            </div>
            <div>
              <Label>Ders (opsiyonel)</Label>
              <Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} data-testid="mock-form-subject" />
            </div>
            <div>
              <Label>Süre (dk)</Label>
              <Input type="number" value={form.duration_minutes} onChange={(e) => setF("duration_minutes", e.target.value)} data-testid="mock-form-duration" />
            </div>
            <div>
              <Label>Başlangıç</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setF("start_at", e.target.value)} data-testid="mock-form-start" />
            </div>
            <div>
              <Label>Bitiş</Label>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => setF("end_at", e.target.value)} data-testid="mock-form-end" />
            </div>
            <div>
              <Label>Negatif Puan</Label>
              <Input type="number" step="0.01" value={form.negative_marking} onChange={(e) => setF("negative_marking", e.target.value)} data-testid="mock-form-neg" />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <Ticket className="h-4 w-4 text-accent" /> Kredi Maliyeti
              </Label>
              <Input type="number" min="0" value={form.credit_cost} onChange={(e) => setF("credit_cost", e.target.value)} data-testid="mock-form-credit-cost" />
              <div className="text-[11px] text-muted-foreground mt-1">
                0 = ücretsiz. &gt;0 girilirse öğrenci katılırken bu kadar kredi düşülür.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} disabled={busy || !form.title} data-testid="mock-form-save">
              <Save className="h-4 w-4" /> {busy ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
